import { type ComponentProps, createContext, useContext, useMemo } from 'react'
import { Drawer as DrawerPrimitive } from '@base-ui/react/drawer'

import { cn } from '../../lib/utils'

type DrawerContextProps = {
  hasSnapPoints: boolean
  modal: DrawerPrimitive.Root.Props['modal']
  showSwipeHandle: boolean
  swipeDirection: NonNullable<DrawerPrimitive.Root.Props['swipeDirection']>
}

const DrawerContext = createContext<DrawerContextProps | null>(null)

const useDrawer = () => {
  const context = useContext(DrawerContext)

  if (!context) throw new Error('useDrawer must be used within a Drawer.')

  return context
}

const Drawer = ({
  modal = true,
  showSwipeHandle = true,
  snapPoints,
  swipeDirection = 'down',
  ...props
}: DrawerPrimitive.Root.Props & {
  showSwipeHandle?: boolean
}) => {
  const hasSnapPoints = snapPoints != null && snapPoints.length > 0
  const contextValue = useMemo(
    () => ({ hasSnapPoints, modal, showSwipeHandle, swipeDirection }),
    [hasSnapPoints, modal, showSwipeHandle, swipeDirection],
  )

  return (
    <DrawerContext.Provider value={contextValue}>
      <DrawerPrimitive.Root
        data-slot="drawer"
        modal={modal}
        snapPoints={snapPoints}
        swipeDirection={swipeDirection}
        {...props}
      />
    </DrawerContext.Provider>
  )
}

const DrawerTrigger = ({ ...props }: DrawerPrimitive.Trigger.Props) => (
  <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />
)

const DrawerPortal = ({ ...props }: DrawerPrimitive.Portal.Props) => (
  <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />
)

const DrawerClose = ({ ...props }: DrawerPrimitive.Close.Props) => (
  <DrawerPrimitive.Close data-slot="drawer-close" {...props} />
)

const DrawerOverlay = ({ className, ...props }: DrawerPrimitive.Backdrop.Props) => (
  <DrawerPrimitive.Backdrop
    className={cn(
      'fixed inset-0 z-50 min-h-dvh bg-black/30 opacity-[max(var(--drawer-overlay-min-opacity,0),calc(1-var(--drawer-swipe-progress)))] transition-opacity duration-1000 select-none',
      '[transition-timing-function:cubic-bezier(0.32,0.72,0,1)]',
      'data-[ending-style]:pointer-events-none data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
      'data-[ending-style]:[transition-duration:calc(var(--drawer-swipe-strength)*400ms)] data-[swiping]:[transition-duration:0ms]',
      'data-[snap-points]:[--drawer-overlay-min-opacity:0.5]',
      'supports-[backdrop-filter]:backdrop-blur-sm supports-[webkit-touch-callout:none]:absolute',
      className,
    )}
    data-slot="drawer-overlay"
    {...props}
  />
)

const DrawerSwipeHandle = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    aria-hidden="true"
    className={cn(
      'after:bg-muted relative z-10 flex shrink-0 cursor-grab transition-opacity duration-200 active:cursor-grabbing',
      'group-data-[nested-drawer-open]/drawer-popup:opacity-0 group-data-[nested-drawer-swiping]/drawer-popup:opacity-100',
      'group-data-[swipe-axis=x]/drawer-popup:h-full group-data-[swipe-axis=x]/drawer-popup:w-3 group-data-[swipe-axis=x]/drawer-popup:items-center',
      'group-data-[swipe-axis=y]/drawer-popup:h-3 group-data-[swipe-axis=y]/drawer-popup:w-full group-data-[swipe-axis=y]/drawer-popup:justify-center',
      'group-data-[swipe-direction=down]/drawer-popup:items-end group-data-[swipe-direction=left]/drawer-popup:order-last group-data-[swipe-direction=left]/drawer-popup:justify-start',
      'group-data-[swipe-direction=right]/drawer-popup:justify-end group-data-[swipe-direction=up]/drawer-popup:order-last group-data-[swipe-direction=up]/drawer-popup:items-start',
      'after:block after:shrink-0 after:rounded-full',
      'group-data-[swipe-axis=x]/drawer-popup:after:h-[100px] group-data-[swipe-axis=x]/drawer-popup:after:w-1.5',
      'group-data-[swipe-axis=y]/drawer-popup:after:h-1.5 group-data-[swipe-axis=y]/drawer-popup:after:w-[100px]',
      className,
    )}
    data-slot="drawer-swipe-handle"
    {...props}
  />
)

const DrawerContent = ({ className, children, ...props }: DrawerPrimitive.Popup.Props) => {
  const { hasSnapPoints, modal, showSwipeHandle, swipeDirection } = useDrawer()
  const swipeAxis = swipeDirection === 'down' || swipeDirection === 'up' ? 'y' : 'x'

  return (
    <DrawerPortal data-slot="drawer-portal">
      {modal === true && <DrawerOverlay data-snap-points={hasSnapPoints ? '' : undefined} />}
      <DrawerPrimitive.Viewport
        className="pointer-events-none fixed inset-0 z-50 select-none data-[modal=true]:pointer-events-auto"
        data-modal={modal}
        data-slot="drawer-viewport"
      >
        <DrawerPrimitive.Popup
          className={cn(
            // Base.
            'group/drawer-popup border-popover bg-popover text-popover-foreground dark:border-border pointer-events-auto fixed z-50 flex min-h-0 flex-col rounded-[24px] border text-sm shadow-xl will-change-transform outline-none select-none',
            'm-[var(--drawer-inset,0px)] h-[var(--drawer-content-height)] max-h-[var(--drawer-content-max-height,none)] w-[var(--drawer-content-width,auto)]',
            '[transform:translate3d(var(--translate-x,0px),var(--translate-y,0px),0)_scale(var(--stack-scale))]',
            'transition-[transform,height,opacity,filter] [transition-duration:450ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
            '[--drawer-bleed-background:transparent] [--drawer-inset:0.5rem]',
            '[--drawer-stacked-shadow:0_-20px_25px_-5px_rgb(0_0_0/0.1),0_-8px_10px_-6px_rgb(0_0_0/0.1)]',
            '[interpolate-size:allow-keywords]',
            'data-[swipe-direction=down]:data-[nested-drawer-open]:[box-shadow:var(--drawer-stacked-shadow)]',
            // Nested.
            'data-[nested-drawer-open]:overflow-hidden data-[nested-drawer-open]:brightness-95',
            // Bleed.
            'after:pointer-events-none after:absolute after:bg-[var(--drawer-bleed-background,hsl(var(--popover)))]',
            'data-[swipe-axis=x]:after:inset-y-0 data-[swipe-axis=x]:after:w-[var(--bleed)]',
            'data-[swipe-axis=y]:after:inset-x-0 data-[swipe-axis=y]:after:h-[var(--bleed)]',
            'data-[swipe-direction=down]:after:top-full data-[swipe-direction=left]:after:right-full',
            'data-[swipe-direction=right]:after:left-full data-[swipe-direction=up]:after:bottom-full',
            // Sizing.
            '[--drawer-content-height:var(--drawer-height,auto)]',
            'data-[swipe-axis=x]:[--drawer-content-width:75%] data-[swipe-axis=y]:[--drawer-content-max-height:calc(100dvh-6rem)]',
            'data-[swipe-axis=y]:data-[snap-points]:[--drawer-content-height:100dvh] data-[swipe-axis=x]:sm:[--drawer-content-width:24rem]',
            // Stack.
            '[--bleed:3rem] [--peek:1rem]',
            '[--stack-height:var(--drawer-frontmost-height,var(--drawer-height,0px))]',
            '[--stack-peek-offset:max(0px,calc((var(--nested-drawers)-var(--stack-progress))*var(--peek)))]',
            '[--stack-progress:clamp(0,var(--drawer-swipe-progress),1)]',
            '[--stack-scale-base:max(0,calc(1-(var(--nested-drawers)*var(--stack-step))))]',
            '[--stack-scale:clamp(0,calc(var(--stack-scale-base)+(var(--stack-step)*var(--stack-progress))),1)]',
            '[--stack-shrink:calc(1-var(--stack-scale))] [--stack-step:0.05]',
            // Transitions.
            'data-[ending-style]:[transform:var(--closed-transform)] data-[starting-style]:[transform:var(--closed-transform)]',
            'data-[ending-style]:opacity-[0.9999]',
            'data-[ending-style]:[transition-duration:calc(var(--drawer-swipe-strength)*400ms)]',
            'data-[nested-drawer-swiping]:[transition-duration:0ms] data-[swiping]:[transition-duration:0ms]',
            'data-[ending-style]:data-[nested-drawer-swiping]:[transition-duration:calc(var(--drawer-swipe-strength)*400ms)]',
            'data-[ending-style]:data-[swiping]:[transition-duration:calc(var(--drawer-swipe-strength)*400ms)]',
            // Axis: y.
            'data-[swipe-axis=y]:inset-x-0 data-[swipe-axis=y]:data-[nested-drawer-open]:h-[var(--stack-height)]',
            // Axis: x.
            'data-[swipe-axis=x]:inset-y-0 data-[swipe-axis=x]:flex-row',
            // Direction: down.
            'data-[swipe-direction=down]:bottom-0 data-[swipe-direction=down]:origin-bottom',
            'data-[swipe-direction=down]:[--closed-transform:translate3d(0,calc(100%+var(--drawer-inset,0px)+2px),0)]',
            'data-[swipe-direction=down]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)-var(--stack-peek-offset)-(var(--stack-shrink)*var(--stack-height)))]',
            // Direction: up.
            'data-[swipe-direction=up]:top-0 data-[swipe-direction=up]:origin-top',
            'data-[swipe-direction=up]:[--closed-transform:translate3d(0,calc(-100%-var(--drawer-inset,0px)-2px),0)]',
            'data-[swipe-direction=up]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)+var(--stack-peek-offset)+(var(--stack-shrink)*var(--stack-height)))]',
            // Direction: left.
            'data-[swipe-direction=left]:left-0 data-[swipe-direction=left]:origin-left',
            'data-[swipe-direction=left]:[--closed-transform:translate3d(calc(-100%-var(--drawer-inset,0px)-2px),0,0)]',
            'data-[swipe-direction=left]:[--translate-x:calc(var(--drawer-swipe-movement-x)+var(--stack-peek-offset)+(var(--stack-shrink)*100%))]',
            // Direction: right.
            'data-[swipe-direction=right]:right-0 data-[swipe-direction=right]:origin-right',
            'data-[swipe-direction=right]:[--closed-transform:translate3d(calc(100%+var(--drawer-inset,0px)+2px),0,0)]',
            'data-[swipe-direction=right]:[--translate-x:calc(var(--drawer-swipe-movement-x)-var(--stack-peek-offset)-(var(--stack-shrink)*100%))]',
            className,
          )}
          data-slot="drawer-popup"
          data-snap-points={hasSnapPoints ? '' : undefined}
          data-swipe-axis={swipeAxis}
          {...props}
        >
          {showSwipeHandle && <DrawerSwipeHandle />}
          <DrawerPrimitive.Content
            className={cn(
              'flex min-h-0 flex-1 flex-col overflow-hidden overscroll-contain rounded-[inherit] transition-opacity duration-300 select-text',
              '[transition-timing-function:cubic-bezier(0.45,1.005,0,1.005)]',
              'group-data-[nested-drawer-open]/drawer-popup:opacity-0 group-data-[nested-drawer-swiping]/drawer-popup:opacity-100',
              'group-data-[swiping]/drawer-popup:select-none',
            )}
            data-slot="drawer-content"
          >
            {children}
          </DrawerPrimitive.Content>
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Viewport>
    </DrawerPortal>
  )
}

const DrawerHeader = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    className={cn(
      'flex shrink-0 flex-col gap-0.5 p-4 pb-0 group-data-[swipe-axis=y]/drawer-popup:text-center md:gap-1.5 md:text-left',
      className,
    )}
    data-slot="drawer-header"
    {...props}
  />
)

const DrawerFooter = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    className={cn('mt-auto flex shrink-0 flex-col gap-2 p-4 pt-0', className)}
    data-slot="drawer-footer"
    {...props}
  />
)

const DrawerTitle = ({ className, ...props }: DrawerPrimitive.Title.Props) => (
  <DrawerPrimitive.Title
    className={cn('text-foreground text-base font-medium', className)}
    data-slot="drawer-title"
    {...props}
  />
)

const DrawerDescription = ({ className, ...props }: DrawerPrimitive.Description.Props) => (
  <DrawerPrimitive.Description
    className={cn('text-muted-foreground text-sm text-balance', className)}
    data-slot="drawer-description"
    {...props}
  />
)

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerSwipeHandle,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
